import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SearchInput } from "@/components/ui/search-input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLabels } from "@/hooks/use-labels";
import { useBulkUpdateMailboxLabels } from "@/hooks/use-mailbox";
import {
  useAddDomainToDns,
  useAddLabelsToPostmasterDomain,
  useRemoveDomainFromDns,
  useRemoveLabelsFromPostmasterDomains,
} from "@/hooks/use-postmaster";
import {
  useAddLabelsToTemplates,
  useRemoveLabelsFromTemplates,
} from "@/hooks/use-template";
import { cn } from "@/lib/utils";
import { ILabel, LabelType } from "@/types/label.types";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Tag, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AddLabelDialog } from "./add-labels";

export type AssignedLabel = Pick<ILabel, "_id" | "name" | "color">;

type SelectedRow = {
  _id: string;
  labels: AssignedLabel[];
};

interface ManageLabelsProps {
  labelType: LabelType; // Fixed typo: lableType -> labelType
  selectedRows?: SelectedRow[];
  className?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  afterChange?: () => void;
}

const ManageLabels = ({
  labelType, // Fixed prop name
  selectedRows,
  className,
  open,
  onOpenChange,
  afterChange,
}: ManageLabelsProps) => {
  // mailbox
  const mailboxMutations = useBulkUpdateMailboxLabels();
  // postmaster
  const postmasterAddMutations = useAddLabelsToPostmasterDomain();
  const postmasterRemoveMutations = useRemoveLabelsFromPostmasterDomains();
  // dns
  const dnsAddMutations = useAddDomainToDns();
  const dnsRemoveMutations = useRemoveDomainFromDns();
  // template
  const templateAddMutations = useAddLabelsToTemplates();
  const templateRemoveMutations = useRemoveLabelsFromTemplates();

  const allUniqueLabels = useMemo(() => {
    if (!selectedRows) return [];

    const labelMap = new Map<string, AssignedLabel>();

    selectedRows.forEach((row: SelectedRow) => {
      row.labels.forEach((label) => {
        // Use _id as the key to ensure uniqueness
        if (!labelMap.has(label._id)) {
          labelMap.set(label._id, label);
        }
      });
    });

    return Array.from(labelMap.values());
  }, [selectedRows]);

  const commonLabels = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return [];

    // Get all unique label IDs from the first row
    const firstRowLabelIds = new Set(
      selectedRows[0].labels.map((label) => label._id)
    );

    // Find labels that exist in all rows
    const commonLabelIds = selectedRows.slice(1).reduce((commonIds, row) => {
      const rowLabelIds = new Set(row.labels.map((label) => label._id));
      return new Set([...commonIds].filter((id) => rowLabelIds.has(id)));
    }, firstRowLabelIds);

    // Convert back to label objects
    return allUniqueLabels.filter((label) => commonLabelIds.has(label._id));
  }, [selectedRows, allUniqueLabels]);

  const handleSubmit = async ({
    removedLabels,
    addedLabels,
  }: {
    removedLabels: string[];
    addedLabels: string[];
  }) => {
    const selectedIds = selectedRows?.map((row: SelectedRow) => row._id);
    if (!selectedIds) {
      toast.error("No selected rows");
      return;
    }

    try {
      if (labelType === LabelType.DNS) {
        if (addedLabels.length > 0) {
          await dnsAddMutations.mutateAsync({
            domain: selectedIds,
            labelIds: addedLabels,
          });
        }
        if (removedLabels.length > 0) {
          await dnsRemoveMutations.mutateAsync({
            domain: selectedIds,
            labelIds: removedLabels,
          });
        }
      }

      if (labelType === LabelType.MAILBOX) {
        if (addedLabels.length > 0) {
          await mailboxMutations.mutateAsync({
            ids: selectedIds,
            labelIds: addedLabels,
            operation: "add",
          });
        }
        if (removedLabels.length > 0) {
          await mailboxMutations.mutateAsync({
            ids: selectedIds,
            labelIds: removedLabels,
            operation: "remove",
          });
        }
      }
      if (labelType === LabelType.POSTMASTER) {
        if (addedLabels.length > 0) {
          await postmasterAddMutations.mutateAsync({
            domain: selectedIds,
            labelIds: addedLabels,
          });
        }
        if (removedLabels.length > 0) {
          await postmasterRemoveMutations.mutateAsync({
            domains: selectedIds,
            labelIds: removedLabels,
          });
        }
      }
      if (labelType === LabelType.TEMPLATE) {
        if (addedLabels.length > 0) {
          await templateAddMutations.mutateAsync({
            templateIds: selectedIds,
            labelIds: addedLabels,
          });
        }
        if (removedLabels.length > 0) {
          await templateRemoveMutations.mutateAsync({
            templateIds: selectedIds,
            labelIds: removedLabels,
          });
        }
      }

      toast.success("Labels updated successfully");
      afterChange?.();
    } catch (error) {
      console.error("Error updating labels:", error);
      toast.error("Failed to update labels");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Labels</DialogTitle>
        </DialogHeader>
        <ManageLabelsContent
          availableLabels={allUniqueLabels}
          commonLabels={commonLabels}
          labelType={labelType} // Fixed prop name
          onSubmit={handleSubmit}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

interface ManageLabelsContentProps {
  availableLabels: AssignedLabel[];
  commonLabels: AssignedLabel[];
  labelType: LabelType; // Fixed prop name
  onSubmit: ({
    removedLabels,
    addedLabels,
  }: {
    removedLabels: string[];
    addedLabels: string[];
  }) => void;
  onClose?: () => void;
}

const ManageLabelsContent = ({
  availableLabels,
  commonLabels,
  labelType, // Fixed prop name
  onSubmit,
  onClose,
}: ManageLabelsContentProps) => {
  const [newSelectedLabels, setNewSelectedLabels] = useState<string[]>([]);
  const [type, setType] = useState<"all" | "common">("all");

  const [showAddLabel, setShowAddLabel] = useState(false);
  const [removedLabels, setRemovedLabels] = useState<string[]>([]);
  const [addedLabels, setAddedLabels] = useState<string[]>([]);

  const { data: labels, isLoading } = useLabels(1, 100, labelType, true); // Fixed variable name

  const queryClient = useQueryClient();

  const currentLabelIds = useMemo(
    () => availableLabels.map((label) => label._id),
    [availableLabels]
  );
  const commonLabelIds = useMemo(
    () => commonLabels.map((label) => label._id),
    [commonLabels]
  );

  // Filter out labels that are already assigned or being removed
  const availableOptions = useMemo(() => {
    if (!labels) return [];

    return labels.data
      .filter((label) => {
        const isNotCurrentlyAssigned = !commonLabelIds.includes(label._id);
        const isNotBeingRemoved = !removedLabels.includes(label._id);

        return isNotCurrentlyAssigned && isNotBeingRemoved;
      })
      .map((label) => ({
        label: label.name,
        value: label._id,
        color: label.color,
      }));
  }, [labels, commonLabelIds, removedLabels]);

  // Get labels that are currently assigned and not being removed
  const currentLabels = useMemo(() => {
    if (type === "common") {
      return commonLabels.filter((label) => !removedLabels.includes(label._id));
    }
    return availableLabels.filter(
      (label) => !removedLabels.includes(label._id)
    );
  }, [availableLabels, type, commonLabels, removedLabels]);

  // Update added labels when new labels are selected - optimized to prevent unnecessary re-renders
  useEffect(() => {
    const newAddedLabels = newSelectedLabels.filter(
      (id) => !removedLabels.includes(id)
    );
    setAddedLabels(newAddedLabels);
  }, [newSelectedLabels, removedLabels]);

  const handleAfterAddLabel = async (label: ILabel) => {
    console.log({ label });
    queryClient.setQueryData(
      ["labels", { page: 1, limit: 100, labelType: labelType }], // Fixed variable name
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (oldData: any) => {
        console.log({ oldData });

        if (!oldData) return { data: [label] };

        return {
          ...oldData,
          data: [...oldData.data, label],
        };
      }
    );
  };

  const handleRemoveLabel = (labelId: string) => {
    setRemovedLabels((prev) => [...prev, labelId]);
    // If this label was newly added, remove it from added labels
    setAddedLabels((prev) => prev.filter((id) => id !== labelId));
    // If this label was newly selected, remove it from selection
    setNewSelectedLabels((prev) => prev.filter((id) => id !== labelId));
  };

  const handleCancel = () => {
    setRemovedLabels([]);
    setAddedLabels([]);
    setNewSelectedLabels([]);
    onClose?.();
  };

  const handleApplyChanges = () => {
    const finalAddedLabels = addedLabels.filter(
      (id) => !removedLabels.includes(id)
    );
    onSubmit({
      removedLabels,
      addedLabels: finalAddedLabels,
    });
    onClose?.();
  };

  const hasChanges = removedLabels.length > 0 || addedLabels.length > 0;

  return (
    <>
      <div className="space-y-4">
        <div className="space-y-1">
          <div className="text-sm font-medium">Select New Labels</div>
          <SearchableMultiSelect
            options={availableOptions}
            loading={isLoading}
            selectedValues={newSelectedLabels}
            onChange={setNewSelectedLabels}
            placeholder="Select labels"
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Remove Labels</div>
            <Tabs
              value={type}
              onValueChange={(value) => setType(value as "common" | "all")}
            >
              <TabsList className="h-8">
                <TabsTrigger value="all" className="text-xs">
                  All
                </TabsTrigger>
                <TabsTrigger value="common" className="text-xs">
                  Common
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="flex flex-wrap gap-1">
            {currentLabels?.map((label) => (
              <Badge
                key={label._id}
                variant="secondary"
                className="flex items-center gap-1 px-1.5 font-normal text-background rounded-sm cursor-pointer hover:opacity-80"
                style={{
                  backgroundColor: label.color,
                }}
              >
                {label.name}
                <X
                  className="size-3 hover:text-gray-200"
                  onClick={() => handleRemoveLabel(label._id)}
                />
              </Badge>
            ))}
            {currentLabels.length === 0 && (
              <span className="text-sm text-muted-foreground">
                No labels assigned
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            className="border-dashed"
            onClick={() => setShowAddLabel(true)}
          >
            Create New Label
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleApplyChanges}
              disabled={!hasChanges}
            >
              Apply Changes
            </Button>
          </div>
        </div>
      </div>

      {showAddLabel && (
        <AddLabelDialog
          open={showAddLabel}
          labelType={labelType} // Fixed prop name
          onOpenChange={setShowAddLabel}
          onSubmit={handleAfterAddLabel}
        />
      )}
    </>
  );
};

type SearchableMultiSelectProps = {
  options: {
    label: string;
    value: string;
    color?: string;
  }[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  loading?: boolean;
};

export function SearchableMultiSelect({
  options,
  selectedValues,
  onChange,
  label,
  loading = false,
  placeholder = "Select options...",
  className = "",
}: SearchableMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = useMemo(() => {
    if (!options) return [];
    if (searchTerm === "") return options;

    return options.filter((option) => {
      const label = option.label.toLowerCase();
      const search = searchTerm.toLowerCase();

      return label.includes(search);
    });
  }, [options, searchTerm]);

  // Prevent closing when input is clicked
  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleSelect = (optionValue: string) => {
    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((value) => value !== optionValue));
    } else {
      onChange([...selectedValues, optionValue]);
    }
  };

  const handleRemove = (e: React.MouseEvent, optionValue: string) => {
    e.stopPropagation();
    onChange(selectedValues.filter((value) => value !== optionValue));
  };

  const renderSelectedValues = () => {
    if (selectedValues.length === 0) {
      return (
        <span className="text-sm text-muted-foreground">{placeholder}</span>
      );
    }

    return (
      <div className="flex flex-wrap items-center gap-1 max-h-[100px] overflow-y-auto">
        {selectedValues.map((value) => {
          const option = options.find((opt) => opt.value === value);
          return (
            <Badge
              key={value}
              variant="secondary"
              className="flex items-center gap-1 px-1.5 font-normal text-background rounded-sm"
              style={{
                backgroundColor: option?.color,
              }}
            >
              {option?.label || value}
              {/* <X
                className="size-3 hover:text-gray-200 cursor-pointer"
                onClick={(e) => handleRemove(e, value)}
              /> */}
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <div className={cn("space-y-1 w-full", className)}>
      {label && <Label>{label}</Label>}
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            className="flex items-center justify-between hover:bg-transparent gap-1 min-h-8 py-1 px-2 border rounded-md cursor-pointer hover:border-primary/50 w-full"
            variant="outline"
            role="combobox"
          >
            {renderSelectedValues()}
            <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-[var(--radix-dropdown-menu-trigger-width)] p-0"
          align="start"
          sideOffset={4}
        >
          <div className="p-2 border-b">
            <SearchInput
              onSearch={(value) => setSearchTerm(value)}
              placeholder="Search..."
              className="max-w-none sm:w-full"
              delay={500}
            />
          </div>
          <ScrollArea className="h-[150px]">
            <div className="p-2 space-y-1">
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mr-2"></div>
                  <span className="text-sm text-muted-foreground">
                    Loading...
                  </span>
                </div>
              ) : filteredOptions?.length === 0 ? (
                <div className="p-2 text-sm text-muted-foreground text-center">
                  No results found
                </div>
              ) : (
                filteredOptions?.map((option, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 px-2 py-1 hover:bg-muted rounded-md cursor-pointer"
                    onClick={() => handleSelect(option.value)}
                    title={option.label}
                  >
                    <Checkbox
                      checked={selectedValues.includes(option.value)}
                      onCheckedChange={() => {}}
                      className="border-gray-300 [data-state=checked]:border-accent"
                    />

                    <Label
                      className={cn("flex-grow cursor-pointer font-normal")}
                    >
                      <Badge
                        className={cn(
                          "text-background max-w-60 shadow-none rounded-sm font-normal"
                        )}
                        style={{
                          backgroundColor: option.color ?? "gray",
                        }}
                      >
                        <Tag className="size-3 fill-current mr-1" />
                        <span className="truncate">{option.label}</span>
                      </Badge>
                    </Label>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export { ManageLabelsContent };
export type { ManageLabelsContentProps, ManageLabelsProps };

export default ManageLabels;
